import { Request, Response, NextFunction } from 'express'
import { RateLimiter, generateRateLimitKey, generateRateLimitHeaders, RateLimitKeyOptions } from './rate-limiter'
import { RateLimit, VerifyApiKeyResult } from '../types'
import { RateLimitError } from '../errors'

/**
 * Configured rate limit middleware options
 */
export interface RateLimitMiddlewareOptions {
  rateLimiter: RateLimiter

  // Function to determine rate limit for a request
  // Return null to skip rate limiting for this request
  getRateLimitForRequest?: (req: Request) => RateLimit | null

  // Function to extract rate limit key from request
  // Determines which principal/entity the limit applies to
  getKeyForRequest?: (req: Request) => RateLimitKeyOptions

  // Skip rate limiting for certain routes
  skip?: (req: Request) => boolean

  // Called before rate limit is enforced (for logging/monitoring)
  onBefore?: (req: Request, key: string, rateLimit: RateLimit) => Promise<void>

  // Called after rate limit is checked successfully
  onSuccess?: (req: Request, key: string) => Promise<void>

  // Called when rate limit is exceeded
  onLimitExceeded?: (req: Request, key: string, error: RateLimitError) => Promise<void>
}

/**
 * Create Express middleware for rate limiting
 * Integrates with authentication context and per-plan quotas
 */
export function createRateLimitMiddleware(options: RateLimitMiddlewareOptions) {
  const {
    rateLimiter,
    getRateLimitForRequest = getDefaultRateLimit,
    getKeyForRequest = getDefaultKey,
    skip,
    onBefore,
    onSuccess,
    onLimitExceeded
  } = options

  return async (req: Request, res: Response, next: NextFunction) => {
    try {
      // Skip rate limiting if configured
      if (skip && skip(req)) {
        return next()
      }

      // Get rate limit for this request
      const rateLimit = getRateLimitForRequest(req)
      if (!rateLimit) {
        return next()
      }

      // Get rate limit key
      const keyOptions = getKeyForRequest(req)
      const key = generateRateLimitKey(keyOptions)

      // Emit before event
      if (onBefore) {
        await onBefore(req, key, rateLimit)
      }

      // Check rate limit
      const quota = await rateLimiter.checkLimit(key, rateLimit)

      // Add rate limit info to request
      ;(req as any).rateLimit = quota
      ;(req as any).rateLimitKey = key

      // Set rate limit headers
      const headers = generateRateLimitHeaders(quota)
      Object.entries(headers).forEach(([name, value]) => {
        if (value) {
          res.setHeader(name, value)
        }
      })

      // Emit success event
      if (onSuccess) {
        await onSuccess(req, key)
      }

      next()
    } catch (error) {
      if (error instanceof RateLimitError) {
        // Set rate limit headers
        const retryAfter = Math.ceil((error.resetAt.getTime() - Date.now()) / 1000)
        res.setHeader('Retry-After', String(Math.max(1, retryAfter)))
        res.setHeader('X-RateLimit-Reset', String(Math.floor(error.resetAt.getTime() / 1000)))

        // Emit limit exceeded event
        if (onLimitExceeded) {
          const key = (req as any).rateLimitKey || 'unknown'
          await onLimitExceeded(req, key, error)
        }

        return res.status(429).json({
          error: 'RATE_LIMIT_EXCEEDED',
          message: error.message,
          retryAfter,
          resetAt: error.resetAt.toISOString()
        })
      }

      next(error)
    }
  }
}

/**
 * Default rate limit getter - uses API key or session rate limit
 */
export function getDefaultRateLimit(req: Request): RateLimit | null {
  // Check if rate limit is attached from API key verification
  const apiKeyVerification = (req as any).apiKey as VerifyApiKeyResult
  if (apiKeyVerification?.rateLimit) {
    return apiKeyVerification.rateLimit
  }

  // Check if rate limit is attached from session
  const session = (req as any).kitiumSession
  if (session?.rateLimit) {
    return session.rateLimit
  }

  // Return null to skip rate limiting if no auth context
  return null
}

/**
 * Default key getter - uses principal ID from auth context
 */
export function getDefaultKey(req: Request): RateLimitKeyOptions {
  // Try API key first
  const apiKeyVerification = (req as any).apiKey as VerifyApiKeyResult
  if (apiKeyVerification?.principalId) {
    return {
      principalId: apiKeyVerification.principalId,
      apiKeyId: apiKeyVerification.keyId
    }
  }

  // Try session
  const session = (req as any).kitiumSession
  if (session?.userId) {
    return {
      principalId: session.userId,
      userId: session.userId,
      orgId: session.orgId
    }
  }

  // Fallback to IP address
  const ipAddress = req.ip || req.socket.remoteAddress || 'unknown'
  return { ipAddress }
}

/**
 * Create a strict rate limit middleware that always enforces a limit
 * Useful for public endpoints that don't require authentication
 */
export function createPublicRateLimitMiddleware(
  rateLimiter: RateLimiter,
  rateLimit: RateLimit,
  options?: Partial<RateLimitMiddlewareOptions>
) {
  return createRateLimitMiddleware({
    rateLimiter,
    getRateLimitForRequest: () => rateLimit,
    getKeyForRequest: (req) => ({
      ipAddress: req.ip || req.socket.remoteAddress || 'unknown'
    }),
    ...options
  })
}

/**
 * Create a per-user rate limit middleware
 * Uses session user ID or API key principal ID
 */
export function createPerPrincipalRateLimitMiddleware(
  rateLimiter: RateLimiter,
  baseLimits: Record<string, RateLimit>,
  options?: Partial<RateLimitMiddlewareOptions>
) {
  return createRateLimitMiddleware({
    rateLimiter,
    getRateLimitForRequest: (req) => {
      const apiKeyVerification = (req as any).apiKey as VerifyApiKeyResult
      if (apiKeyVerification?.plan) {
        return baseLimits[apiKeyVerification.plan] || baseLimits.free
      }

      const session = (req as any).kitiumSession
      if (session?.plan) {
        return baseLimits[session.plan] || baseLimits.free
      }

      // No authenticated user, no rate limit
      return null
    },
    getKeyForRequest: getDefaultKey,
    ...options
  })
}

/**
 * Create an endpoint-specific rate limit middleware
 * Applies same limit to all callers regardless of plan
 */
export function createEndpointRateLimitMiddleware(
  rateLimiter: RateLimiter,
  rateLimit: RateLimit,
  options?: Partial<RateLimitMiddlewareOptions>
) {
  return createRateLimitMiddleware({
    rateLimiter,
    getRateLimitForRequest: () => rateLimit,
    getKeyForRequest: getDefaultKey,
    ...options
  })
}

/**
 * Declare rate limit info on Request for TypeScript
 */
declare global {
  namespace Express {
    interface Request {
      rateLimit?: {
        count: number
        resetAt: Date
        limit: number
      }
      rateLimitKey?: string
    }
  }
}
